import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { movieApi } from '../../services/api';

const AdminMovie = () => {
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newGenre, setNewGenre] = useState('');
  const [newCast, setNewCast] = useState('');

  useEffect(() => {
    const fetchMovie = async () => {
      try {
        const res = await movieApi.get();
        setMovie(res.data);
      } catch (error) {
        console.error('Error fetching movie:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMovie();
  }, []);

  const handleChange = (field, value) => {
    setMovie({ ...movie, [field]: value });
  };

  const addGenre = () => {
    if (newGenre.trim()) {
      setMovie({ ...movie, genres: [...(movie.genres || []), newGenre.trim()] });
      setNewGenre('');
    }
  };

  const removeGenre = (index) => {
    setMovie({ ...movie, genres: movie.genres.filter((_, i) => i !== index) });
  };

  const addCast = () => {
    if (newCast.trim()) {
      setMovie({ ...movie, cast: [...(movie.cast || []), newCast.trim()] });
      setNewCast('');
    }
  };

  const removeCast = (index) => {
    setMovie({ ...movie, cast: movie.cast.filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await movieApi.update(movie);
      alert('Película guardada correctamente');
    } catch (error) {
      console.error('Error saving movie:', error);
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Información de la Película</h1>
          <p className="text-gray-500">Gestiona los datos básicos de la película</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#e84c8a] hover:bg-[#d43d7a]">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Información Básica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Título</Label>
              <Input id="title" value={movie?.title || ''} onChange={(e) => handleChange('title', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="subtitle">Subtítulo</Label>
              <Input id="subtitle" value={movie?.subtitle || ''} onChange={(e) => handleChange('subtitle', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="tagline">Tagline</Label>
              <Input id="tagline" value={movie?.tagline || ''} onChange={(e) => handleChange('tagline', e.target.value)} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration">Duración</Label>
                <Input id="duration" value={movie?.duration || ''} onChange={(e) => handleChange('duration', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="rating">Clasificación</Label>
                <Input id="rating" value={movie?.rating || ''} onChange={(e) => handleChange('rating', e.target.value)} className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="releaseDate">Fecha de Estreno</Label>
              <Input id="releaseDate" type="date" value={movie?.releaseDate || ''} onChange={(e) => handleChange('releaseDate', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="director">Director</Label>
              <Input id="director" value={movie?.director || ''} onChange={(e) => handleChange('director', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="trailerUrl">URL del Trailer (YouTube)</Label>
              <Input id="trailerUrl" value={movie?.trailerUrl || ''} onChange={(e) => handleChange('trailerUrl', e.target.value)} className="mt-1" placeholder="https://www.youtube.com/embed/..." />
            </div>
          </CardContent>
        </Card>

        {/* Genres & Cast */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Géneros</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {movie?.genres?.map((genre, index) => (
                  <span key={index} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full text-sm">
                    {genre}
                    <button onClick={() => removeGenre(index)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input placeholder="Nuevo género" value={newGenre} onChange={(e) => setNewGenre(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addGenre()} />
                <Button onClick={addGenre} variant="outline"><Plus className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Reparto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {movie?.cast?.map((actor, index) => (
                  <span key={index} className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full text-sm">
                    {actor}
                    <button onClick={() => removeCast(index)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input placeholder="Nuevo actor" value={newCast} onChange={(e) => setNewCast(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addCast()} />
                <Button onClick={addCast} variant="outline"><Plus className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Estado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="status">Estado de Exhibición</Label>
                <Input id="status" value={movie?.status || ''} onChange={(e) => handleChange('status', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="digitalRelease">Lanzamiento Digital</Label>
                <Input id="digitalRelease" value={movie?.digitalRelease || ''} onChange={(e) => handleChange('digitalRelease', e.target.value)} className="mt-1" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminMovie;
